import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from "react";
import {
  Button,
  Grid,
  Typography,
  Skeleton,
  Stack
} from "@mui/material";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import Modal from "../../../Components/Modal";
import useGlobalContext from "../../../Hooks/useGlobalContext";
import { useSnackbar } from "notistack";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import CompetenciaStorageUtils from "../../../Storages/competenciaStorage";
// Seletor de estabelecimento agora é controlado na página pai (Sped)

interface IPreserveSpedProps {
  reloadResourceTable: () => void;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  establishmentAliasIdSelecionado: string | number;
}

const PreserveSped: React.FC<IPreserveSpedProps> = ({
  reloadResourceTable,
  openModal,
  setOpenModal,
  establishmentAliasIdSelecionado
}) => {
  const { setLoading, loading } = useGlobalContext();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [enableButton, setEnableButton] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setEnableButton(!!selectedFile && !!establishmentAliasIdSelecionado);
  };

  // Habilita ou desabilita o botão sempre que arquivo mudar
  useEffect(() => {
    setEnableButton(!!file && !!establishmentAliasIdSelecionado);
  }, [file, establishmentAliasIdSelecionado]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setFile(null);
    setEnableButton(false);
  };

  const reloadDataForm = () => {
    setFile(null);
    setEnableButton(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleUpload = async () => {
    if (!file) return;

    if (!establishmentAliasIdSelecionado) {
      enqueueSnackbar('Selecione o estabelecimento antes de importar.', { variant: 'warning' });
      return;
    }

    setLoading?.(true);
    const authToken = Cookies.get("authTokenMHP");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("establishment_alias_id", String(establishmentAliasIdSelecionado));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}sped`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (result?.identifier) {
        // Limpa a competência selecionada do localStorage quando novo SPED é importado
        CompetenciaStorageUtils.clear();

        enqueueSnackbar(result.message || "Arquivo importado com sucesso!", {
          variant: "success"
        });
        handleCloseModal();
        reloadResourceTable();

        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        enqueueSnackbar(result?.message || "Erro ao importar arquivo.", {
          variant: "error"
        });
      }
    } catch (error) {
      console.error("Erro ao importar SPED:", error);
      enqueueSnackbar("Erro inesperado ao importar o arquivo.", {
        variant: "error"
      });
    } finally {
      setLoading?.(false);
    }
  };

  useEffect(() => {
    if (openModal) reloadDataForm();
  }, [openModal]);

  return (
    <Modal
      title="Importar novo Arquivo SPED Fiscal"
      open={openModal && !loading}
      closeModal={handleCloseModal}
      maxWidth="sm"
    >
      <Grid container spacing={0.5}>
        <Grid item xs={12}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            aria-label="Selecionar arquivo SPED"
            title="Selecionar arquivo SPED"
          >
            Selecione o arquivo SPED (.txt)
            <input
              type="file"
              accept=".txt"
              hidden
              ref={inputRef}
              onChange={handleFileChange}
            />
          </Button>

          {file && (
            <Typography mt={1} variant="body2" color="textSecondary">
              Arquivo selecionado: <strong>{file.name}</strong>
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              disabled={!enableButton || loading}
              onClick={handleUpload}
              startIcon={
                loading ? (
                  <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: "#FFF" }} />
                ) : (
                  <SaveAsIcon />
                )
              }
              aria-label="Importar arquivo SPED"
            >
              Importar
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default PreserveSped;
